import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconDownload, IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { downloadFile } from '../../utils/download';

interface ChartOfAccountOption {
  id: number;
  code: string;
  name: string;
}

interface OrgUnitRecord {
  id: number;
  parent_org_unit_id: number | null;
  unit_name: string;
  cost_center_account_id: number | null;
  cost_center_account: ChartOfAccountOption | null;
}

interface OrgUnitForm {
  parent_org_unit_id: number | '';
  unit_name: string;
  cost_center_account_id: number | '';
}

const emptyForm: OrgUnitForm = { parent_org_unit_id: '', unit_name: '', cost_center_account_id: '' };

export function OrgUnits() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<OrgUnitRecord[] | null>(null);
  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccountOption[]>([]);
  const [editing, setEditing] = useState<'new' | OrgUnitRecord | null>(null);
  const [form, setForm] = useState<OrgUnitForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [exportingChart, setExportingChart] = useState(false);

  const exportChartPdf = async () => {
    setExportingChart(true);
    try {
      await downloadFile(api, '/org-units/chart/pdf', 'org-chart.pdf');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExportingChart(false);
    }
  };

  const load = () => api.get<OrgUnitRecord[]>('/org-units').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<ChartOfAccountOption[]>('/chart-of-accounts').then((res) => setChartOfAccounts(res.data));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: OrgUnitRecord) => {
    setForm({
      parent_org_unit_id: item.parent_org_unit_id ?? '',
      unit_name: item.unit_name,
      cost_center_account_id: item.cost_center_account_id ?? '',
    });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof OrgUnitForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        parent_org_unit_id: form.parent_org_unit_id || null,
        cost_center_account_id: form.cost_center_account_id || null,
      };
      if (editing === 'new') await api.post('/org-units', payload);
      else if (editing) await api.put(`/org-units/${editing.id}`, payload);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: OrgUnitRecord) => {
    if (!(await confirmDelete(`Delete "${item.unit_name}"?`))) return;
    try {
      await api.delete(`/org-units/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const unitName = (id: number | null) => items?.find((i) => i.id === id)?.unit_name ?? '—';

  return (
    <>
      <header className="page-header">
        <h1>Org Units</h1>
        <div className="page-header-actions">
          <IconButton
            icon={IconDownload}
            label={exportingChart ? 'Exporting…' : 'Export Chart PDF'}
            onClick={exportChartPdf}
            disabled={exportingChart}
          />
          <button type="button" className="btn btn-primary" onClick={openNew}>
            + Add
          </button>
        </div>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Parent</th>
                <th>Cost Center</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.unit_name}</td>
                  <td className="muted">{unitName(item.parent_org_unit_id)}</td>
                  <td className="muted">
                    {item.cost_center_account ? `${item.cost_center_account.code} — ${item.cost_center_account.name}` : '—'}
                  </td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No org units yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add org unit' : 'Edit org unit'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.unit_name} onChange={set('unit_name')} required />
              {errors.unit_name && <small className="error">{errors.unit_name[0]}</small>}
            </label>
            <label className="span-2">
              Parent Org Unit
              <select value={form.parent_org_unit_id} onChange={set('parent_org_unit_id')}>
                <option value="">None</option>
                {items
                  ?.filter((i) => editing === 'new' || i.id !== editing.id)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.unit_name}
                    </option>
                  ))}
              </select>
              {errors.parent_org_unit_id && <small className="error">{errors.parent_org_unit_id[0]}</small>}
            </label>
            <label className="span-2">
              Cost Center (GL Account)
              <select value={form.cost_center_account_id} onChange={set('cost_center_account_id')}>
                <option value="">None</option>
                {chartOfAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
              {errors.cost_center_account_id && <small className="error">{errors.cost_center_account_id[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
